import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api from "@/lib/api";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null); // null = loading, false = anon
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    const token = localStorage.getItem("rh_token");
    if (!token) {
      setUser(false);
      setLoading(false);
      return;
    }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      localStorage.removeItem("rh_token");
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    localStorage.setItem("rh_token", data.token);
    setUser(data.user);
    return data.user;
  };

  // Estabelece a sessão a partir de um token já emitido pelo backend
  // (usado pelo callback do Google Sign-in).
  const loginWithToken = async (token, userData) => {
    localStorage.setItem("rh_token", token);
    if (userData) {
      setUser(userData);
      return userData;
    }
    const { data } = await api.get("/auth/me");
    setUser(data);
    return data;
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch (err) {
      // Non-fatal: server-side logout failure shouldn't block local cleanup.
      console.warn("Logout API call failed (local session cleared anyway):", err);
    }
    localStorage.removeItem("rh_token");
    setUser(false);
  };

  const hasRole = (...roles) => !!user && (user.role === "master" || roles.includes(user.role));
  const canWrite = () => hasRole("rh_admin", "rh_operacional");
  const canAdmin = () => hasRole("rh_admin");

  return (
    <AuthContext.Provider value={{ user, loading, login, loginWithToken, logout, refresh, hasRole, canWrite, canAdmin }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
